import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  Users2,
  LayoutGrid,
  Upload,
  BarChart3,
  Settings2,
  LogOut,
  Bell,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  UserCircle2,
} from "lucide-react";
import { useAuth, hasRole } from "../lib/auth";

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/my-day", label: "My Day", icon: CalendarDays },
  { to: "/leads", label: "Leads", icon: LayoutGrid },
  { to: "/bulk-import", label: "Bulk Import", icon: Upload, roles: ["admin", "sales_manager", "sales_exec", "bd"] },
  {
    label: "Reports",
    icon: BarChart3,
    children: [
      { to: "/reports", label: "Overview" },
      { to: "/reports/target-vs-achievement", label: "Target vs Achievement" },
    ],
  },
  { to: "/settings", label: "Settings", icon: UserCircle2 },
  {
    label: "Admin",
    icon: Settings2,
    roles: ["admin"],
    children: [
      { to: "/admin/masters", label: "Master Data" },
      { to: "/admin/users", label: "Users & Roles" },
      { to: "/admin/targets", label: "Targets" },
      { to: "/admin/api-keys", label: "API Keys" },
      { to: "/admin/email-templates", label: "Email Templates" },
      { to: "/admin/stage-email-rules", label: "Stage Email Triggers" },
      // { to: "/admin/agent", label: "Agent" }, // hidden for now
    ],
  },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState({ Admin: true, Reports: true });

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 shrink-0 bg-white border-r border-slate-200 h-screen sticky top-0 flex flex-col"
    >
      <div className="px-5 py-5 border-b border-slate-100 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 grid place-items-center overflow-hidden">
          <img src="/L360.png" alt="Losung360" className="w-7 h-7 object-contain" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900 tracking-tight">LeadHub</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">LMS Console</div>
        </div>
      </div>

      <nav className="flex-1 p-3 overflow-y-auto scrollbar-thin">
        {NAV.map((item) => {
          if (item.roles && !hasRole(user, ...item.roles)) return null;
          if (item.children) {
            const isOpen = openGroups[item.label] !== false;
            return (
              <div key={item.label} className="mb-1">
                <button
                  data-testid={`nav-${item.label.toLowerCase()}-toggle`}
                  onClick={() => setOpenGroups((g) => ({ ...g, [item.label]: !isOpen }))}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span className="flex items-center gap-2.5">
                    <item.icon size={16} className="text-slate-500" />
                    {item.label}
                  </span>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {isOpen && (
                  <div className="ml-3 mt-1 pl-3 border-l border-slate-100 space-y-1">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end
                        data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `block px-3 py-1.5 rounded-md text-sm transition-colors ${
                            isActive
                              ? "bg-orange-50 text-orange-600 font-medium"
                              : "text-slate-600 hover:bg-slate-50"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 mb-1 rounded-md text-sm transition-all ${
                  isActive
                    ? "bg-orange-500 text-white font-medium shadow-sm"
                    : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-100">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-700 grid place-items-center text-sm font-semibold">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-slate-500 truncate">{user?.role}</div>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
